/**
 * Phase 14 Plan 14-02 — RadioGroup + RadioGroupItem (native HTML implementation).
 *
 * Provides a RadioGroup container and RadioGroupItem that mimic the shadcn/ui
 * RadioGroup API so consuming components (ApproverRejectDialog) do not need
 * @radix-ui/react-radio-group. Uses native <input type="radio"> styled consistently
 * with the project's Tailwind theme.
 *
 * [Rule 3 auto-addition]: RadioGroup is not in the existing UI component set but is
 * required by ApproverRejectDialog for the RouteTo radio pair.
 */

import { createContext, type ChangeEvent, forwardRef, type JSX, useContext, useId } from 'react';

import { cn } from '@/lib/utils/cn';

/* ---- RadioGroupContext ---- */

interface RadioGroupContextValue {
  readonly name: string;
  readonly groupValue: string | undefined;
  readonly onValueChange: ((value: string) => void) | undefined;
}

const RadioGroupContext = createContext<RadioGroupContextValue>({
  name: '',
  groupValue: undefined,
  onValueChange: undefined,
});

/* ---- RadioGroup ---- */

export interface RadioGroupProps {
  readonly value?: string;
  readonly onValueChange?: (value: string) => void;
  readonly className?: string;
  readonly children: React.ReactNode;
  /** data-testid forwarded to the wrapping div. */
  readonly 'data-testid'?: string;
}

export function RadioGroup({
  value,
  onValueChange,
  className,
  children,
  'data-testid': testId,
}: RadioGroupProps): JSX.Element {
  const groupName = useId();
  return (
    <RadioGroupContext.Provider
      value={{ name: groupName, groupValue: value, onValueChange: onValueChange }}
    >
      <div role="radiogroup" className={cn('flex flex-col gap-2', className)} data-testid={testId}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
}

/* ---- RadioGroupItem ---- */

export interface RadioGroupItemProps {
  readonly value: string;
  readonly id?: string;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly 'data-testid'?: string;
}

export const RadioGroupItem = forwardRef<HTMLInputElement, RadioGroupItemProps>(
  function RadioGroupItem(
    { value, id, className, disabled, 'data-testid': testId },
    ref,
  ): JSX.Element {
    const { name, groupValue, onValueChange } = useContext(RadioGroupContext);

    function handleChange(e: ChangeEvent<HTMLInputElement>): void {
      if (e.target.checked && onValueChange !== undefined) {
        onValueChange(value);
      }
    }

    return (
      <input
        ref={ref}
        type="radio"
        id={id}
        name={name}
        value={value}
        checked={groupValue !== undefined ? groupValue === value : undefined}
        onChange={handleChange}
        disabled={disabled}
        data-testid={testId}
        className={cn(
          'h-4 w-4 cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
      />
    );
  },
);
